import { useEffect, useState } from "react";
import type { ProfileSummary } from "../../shared/game.ts";
import { getProfileByFriendCode } from "../lib/api.ts";
import { displayFriendCode } from "../lib/friend-code.ts";
import { isIosSafari } from "../lib/install.ts";
import { Icon } from "./Icon.tsx";

export function FriendInviteLanding({ code }: { code: string }) {
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [copied, setCopied] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const safari = isIosSafari();
  const shownCode = displayFriendCode(code);

  useEffect(() => {
    let cancelled = false;
    void getProfileByFriendCode(code)
      .then((value) => {
        if (!cancelled) setProfile(value);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [code]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(shownCode);
      setCopied(true);
    } catch {
      const input = document.createElement("textarea");
      input.value = shownCode;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.append(input);
      input.select();
      document.execCommand("copy");
      input.remove();
      setCopied(true);
    }
  };

  return (
    <main className="install-gate friend-invite-landing">
      <section className="install-gate-card friend-invite-card">
        <img className="install-app-icon" src="/icons/apple-touch-icon.png" alt="" />
        <span className="eyebrow">Приглашение в друзья</span>
        <h1>{profile ? `${profile.name} приглашает вас` : "Добавьте друга"}</h1>
        <p>На iPhone ссылка открывается в Safari, а не в приложении с экрана «Домой». Поэтому скопируйте код и вставьте его в «Шахматах».</p>

        <button className={`friend-landing-code ${copied ? "copied" : ""}`} type="button" onClick={() => void copyCode()}>
          <span>Код друга</span><strong>{shownCode}</strong><small><Icon name={copied ? "check" : "copy"} /> {copied ? "Скопировано" : "Нажмите, чтобы скопировать"}</small>
        </button>

        <ol className="friend-open-steps">
          <li><b>1</b><span>Скопируйте код</span></li>
          <li><b>2</b><span>Откройте «Шахматы» с экрана «Домой»</span></li>
          <li><b>3</b><span>Нажмите «Друзья → Добавить» и вставьте код</span></li>
        </ol>

        <button className="button button-primary button-large button-full" type="button" onClick={() => void copyCode()}>
          <Icon name={copied ? "check" : "copy"} />{copied ? "Код скопирован" : "Скопировать код"}
        </button>
        <button className="button button-quiet button-full friend-install-help" type="button" onClick={() => setShowSteps(true)}>
          <Icon name="install" />{safari ? "Как установить Шахматы" : "Установить через Safari"}
        </button>
      </section>

      {showSteps && (
        <div className="modal-backdrop install-sheet-backdrop" role="presentation">
          <section className="modal-card install-sheet" role="dialog" aria-modal="true" aria-labelledby="friend-install-title">
            <button className="icon-button modal-close" type="button" onClick={() => setShowSteps(false)} aria-label="Закрыть"><Icon name="x" /></button>
            <img className="install-sheet-icon" src="/icons/apple-touch-icon.png" alt="" />
            <h2 id="friend-install-title">Установить Шахматы</h2>
            <p>{safari ? "Два шага в Safari:" : "Сначала откройте эту ссылку в Safari, затем:"}</p>
            <ol className="install-steps">
              <li><span className="install-step-icon"><Icon name="ios-share" /></span><span><b>Нажмите «Поделиться»</b><small>Кнопка находится в нижней панели Safari.</small></span></li>
              <li><span className="install-step-icon"><Icon name="add-square" /></span><span><b>Выберите «На экран Домой»</b><small>Затем подтвердите кнопкой «Добавить».</small></span></li>
            </ol>
            <button className="button button-primary button-large button-full" type="button" onClick={() => setShowSteps(false)}>Понятно</button>
            <small className="install-launch-note">После установки откройте приложение и вставьте сохранённый код.</small>
          </section>
        </div>
      )}
    </main>
  );
}
